import { useAccount, useBalance, useDisconnect } from 'wagmi'
import { useWeb3Modal } from '@web3modal/wagmi/react'
import { formatEther } from 'viem'
import { useState, useEffect, useCallback } from 'react'
import { getUserByAddress, updateLastLogin } from '../services/userService'
import type { UserRank } from '../types/user'

interface Member {
  address: string;
  amountLocked: string;
}

interface UserData {
  username: string;
  stakedAmount: number;
  totalPoints: number;
  rank: UserRank;
}

export function useWallet() {
  const { address, isConnected } = useAccount()
  const { open } = useWeb3Modal()
  const { disconnect } = useDisconnect()
  const { data: balance } = useBalance({
    address,
  })
  const [needsUsername, setNeedsUsername] = useState(false)
  const [isCheckingUser, setIsCheckingUser] = useState(false)
  const [userData, setUserData] = useState<UserData | null>(null)

  // Function to fetch staked amount from creator.bid API
  const fetchStakedAmount = useCallback(async (walletAddress: string): Promise<number> => {
    try {
      const response = await fetch('https://creator.bid/api/agents/676ef7b23bdebce80077f770/members');
      const data = await response.json();
      
      const member = data.members.find((m: Member) => 
        m.address.toLowerCase() === walletAddress.toLowerCase()
      );
      
      return member ? parseFloat(member.amountLocked) : 0;
    } catch (error) {
      console.error('Error fetching staked amount:', error);
      return 0;
    }
  }, []);

  // Function to refresh user data
  const refreshUserData = useCallback(async () => {
    if (!address) {
      setUserData(null);
      setNeedsUsername(false);
      setIsCheckingUser(false);
      return;
    }

    try {
      const user = await getUserByAddress(address);
      
      if (user) {
        // Update last login time
        await updateLastLogin(address);
        
        // Get latest staked amount
        const stakedAmount = await fetchStakedAmount(address);
        
        setUserData({
          username: user.username,
          stakedAmount,
          totalPoints: user.totalPoints || 0,
          rank: user.rank
        });
        setNeedsUsername(false);
      } else {
        setUserData(null);
        setNeedsUsername(true);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
      setUserData(null);
      setNeedsUsername(false);
    } finally {
      setIsCheckingUser(false);
    }
  }, [address, fetchStakedAmount]);

  // Handle initial connection and address changes
  useEffect(() => {
    if (isConnected && address) {
      setIsCheckingUser(true);
      refreshUserData();
    } else {
      setUserData(null);
      setNeedsUsername(false);
      setIsCheckingUser(false);
    }
  }, [isConnected, address, refreshUserData]);

  // Handle connect
  const handleConnect = useCallback(async () => {
    try {
      await open();
    } catch (error) {
      console.error('Error connecting wallet:', error);
      setIsCheckingUser(false);
    }
  }, [open]);

  // Handle disconnect
  const handleDisconnect = useCallback(async () => {
    try {
      await disconnect();
      setUserData(null);
      setNeedsUsername(false);
      setIsCheckingUser(false);
    } catch (error) {
      console.error('Error disconnecting wallet:', error);
    }
  }, [disconnect]);

  return {
    address,
    isConnected,
    balance: balance ? formatEther(balance.value) : '0',
    symbol: balance?.symbol || 'ETH',
    connect: handleConnect,
    disconnect: handleDisconnect,
    needsUsername,
    isCheckingUser,
    userData,
    refreshUserData
  }
}